import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Toolbar,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Divider,
} from '@mui/material';
import type { StepIconProps } from '@mui/material/StepIcon';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { buscarStatusPedidoPorToken, getLastOrderToken, type PedidoStatusDTO } from '../services/pedidoService';

interface OrdersDialogProps {
  open: boolean;
  onClose: () => void;
}

const ORDER_STATUS_STEPS: string[] = [
  'RECEBIDO',
  'EM_PREPARO',
  'SAIU_PARA_ENTREGA',
  'ENTREGUE',
];

const getOrderStatusStepIndex = (status: string): number => {
  if (!status) return 0;
  const normalized = status.toUpperCase();

  // Mapeia os status de domínio para os passos do fluxo visual
  switch (normalized) {
    case 'RECEBIDO':
      return 0;
    case 'EM_PREPARO':
      return 1;
    // PRONTO / EM_ROTA / SAIU_PARA_ENTREGA são tratados como "saiu para entrega"
    case 'PRONTO':
    case 'EM_ROTA':
    case 'SAIU_PARA_ENTREGA':
      return 2;
    case 'ENTREGUE':
      return 3;
    // CANCELADO e outros caem no primeiro passo por simplicidade
    default:
      return 0;
  }
};

const normalizeStatusLabel = (status: string): string => {
  if (!status) return '';
  return status.replace(/_/g, ' ');
};

const OrderStepIcon = (props: StepIconProps) => {
  const { active, completed, icon } = props;
  const stepIndex = Number(icon) - 1;

  const iconMap: Record<number, React.ReactElement> = {
    0: <MarkEmailReadIcon />, // RECEBIDO
    1: <RestaurantIcon />, // EM_PREPARO
    2: <DeliveryDiningIcon />, // SAIU_PARA_ENTREGA
    3: <CheckCircleIcon />, // ENTREGUE
  };

  const color = completed || active ? '#d32f2f' : '#bdbdbd';

  return (
    <Box
      sx={{
        color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {iconMap[stepIndex] ?? <MarkEmailReadIcon />}
    </Box>
  );
};

const OrdersDialog: React.FC<OrdersDialogProps> = ({ open, onClose }) => {
  const [selectedOrder, setSelectedOrder] = useState<PedidoStatusDTO | null>(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const handleClose = () => {
    onClose();
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) {
      return;
    }

    const handleNoRecentOrder = () => {
      try {
        window.localStorage.removeItem('betterdog_orders_dialog_state_v1');
      } catch {
        // ignore erros de storage
      }
      onClose();
    };

    const fetchStatus = async () => {
      setIsLoadingStatus(true);
      setSelectedOrder(null);
      try {
        const token = getLastOrderToken();
        if (!token) {
          handleNoRecentOrder();
          return;
        }

        try {
          const status = await buscarStatusPedidoPorToken(token);

          if (!status) {
            handleNoRecentOrder();
            return;
          }

          setSelectedOrder(status);
        } catch (error) {
          console.error('[OrdersDialog] Erro ao buscar status do pedido por token:', error);
          handleNoRecentOrder();
        }
      } finally {
        setIsLoadingStatus(false);
      }
    };

    void fetchStatus();
  }, [open, onClose]);

  return (
    <>
      <Dialog fullScreen open={open} onClose={handleClose}>
        <AppBar position="relative" color="inherit" elevation={1}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="voltar">
              <ArrowBackIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              Status do pedido
            </Typography>
          </Toolbar>
        </AppBar>

        <DialogContent sx={{ p: 2 }}>
          {isLoadingStatus ? (
            <Typography variant="body2" color="text.secondary">
              Carregando status do último pedido...
            </Typography>
          ) : !selectedOrder ? (
            <Typography variant="body2" color="text.secondary">
              Nenhum pedido recente encontrado para rastreamento.
            </Typography>
          ) : (
            <Box sx={{ mt: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 0.5,
                  fontWeight: 'bold',
                }}
              >
                Pedido {selectedOrder.id}
              </Typography>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Status do pedido
              </Typography>
              <Stepper
                activeStep={getOrderStatusStepIndex(selectedOrder.status)}
                alternativeLabel
                sx={{
                  mb: 2,
                  '& .MuiStepIcon-root.Mui-active': {
                    color: '#d32f2f',
                  },
                  '& .MuiStepIcon-root.Mui-completed': {
                    color: '#d32f2f',
                  },
                }}
              >
                {ORDER_STATUS_STEPS.map((label) => (
                  <Step key={label}>
                    <StepLabel
                      StepIconComponent={OrderStepIcon}
                      sx={{
                        '& .MuiStepLabel-label': {
                          mt: 1,
                          fontSize: '0.7rem',
                          textTransform: 'uppercase',
                        },
                      }}
                    >
                      {normalizeStatusLabel(label)}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>

              <Box sx={{ mt: 1 }}>
                <Typography variant="body2">
                  <strong>Status atual:</strong> {normalizeStatusLabel(selectedOrder.status)}
                </Typography>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Divider sx={{ mb: 1 }} />
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Forma de pagamento:</strong> {selectedOrder.formaPagamento || 'Não informado'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Valor itens:</strong> R$ {selectedOrder.valorItens.toFixed(2)}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Taxa de entrega:</strong> R$ {selectedOrder.taxaEntrega.toFixed(2)}
                </Typography>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Divider sx={{ mb: 1 }} />
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  <strong>Total:</strong> R$ {(selectedOrder.valorItens + selectedOrder.taxaEntrega).toFixed(2)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default OrdersDialog;
